import { z, type ZodType } from "zod";

import { looseUuid, shortTextSchema } from "@thalamos/common";

import { IncidentTypeSchema } from "./incidentType.js";
import { OrganisationRoleSchema } from "./organisation.js";

/**
 * A dispatch number
 */
export const dispatchNumberSchema = z
  .string()
  .min(4, "fieldSchema.dispatchNumber.tooShort")
  .max(255);

/**
 * A command unit
 */
export const commandUnitSchema = looseUuid();

/**
 * A Vision Organisation
 */
export const availableOrganisationSchema = z.object({
  id: looseUuid(),
  name: z.string(),
  managingOrganisationId: looseUuid().nullable(),
  role: OrganisationRoleSchema,
});

export const availableOrganisationsArraySchema =
  availableOrganisationSchema.array();

export const clinicalCaseRecordNumberSchema = shortTextSchema;
/**
 * Ways of contacting the NHS
 */
export const nhsContactResults = [
  "controlRoom",
  "triageCarHO",
  "triageCarBtp",
  "healthcarePractitionerAmbulance",
  "ldPractitionersInCustody",
  "custodyHealthcarePractitioner",
  "panLondonMentalHealthAdviceLine",
  "localMentalHealthAdviceLine",
  "localHbpos",
  "localStreetTriageTeam",
  "mentalHealthTeamOther",
  "nhs111",
  "nhsStaffNwr",
  "nhsStaffAE",
  "amph",
  "other",
] as const;

export const nhsContactSchema = z.enum(nhsContactResults);

export const otherActionResults = [
  "arrestCriminalOffence",
  "watchList",
  "referSupportOptions",
  "other",
] as const;

export const otherActionSchema = z.enum(otherActionResults);

export const voluntaryActionResults = [
  "voluntaryAttendanceHospital",
  "divertOtherHealthservice",
  "contactSupportGroup",
  "contactFriendsFamily",
  "takePersonHome",
  "divertCrisisCafe",
  "other",
] as const;

export const voluntaryActionSchema = z.enum(voluntaryActionResults);

export const givenHealthCareProfessionalAdviceResults = [
  IncidentTypeSchema.enum.section136,
  IncidentTypeSchema.enum.mentalCapacityAct,
  IncidentTypeSchema.enum.voluntary,
  IncidentTypeSchema.enum.other,
] as const;

export const givenHealthCareProfessionalAdviceSchema = z.enum(
  givenHealthCareProfessionalAdviceResults,
);

/**
 * Triage outcome
 * TODO: Add more options: https://thalamos.atlassian.net/wiki/spaces/P2/pages/1950744580/Incident+Triage+Page
 */
export const triageOutcomeSchema = z.enum([
  "136" as const,
  "mca" as const,
  "voluntary" as const,
  "other" as const,
]);

/**
 * This will be deprecated once the FieldBuilder is in place
 *
 * @param valueSchema
 * @returns
 */
export const valueAndLabelSchemaDeprecated = <T extends ZodType>(
  valueSchema: T,
) =>
  z.object({
    value: valueSchema,
    valueLabel: z.string(),
  });

export const genderOptions = [
  "male",
  "female",
  "nonBinary",
  "preferNotToSay",
] as const;

export const genderSchema = z.enum(genderOptions);
export type GenderType = z.infer<typeof genderSchema>;

export const mcaGenderSchema = z.enum([...genderOptions, "other"] as const);
export type McaGenderType = z.infer<typeof mcaGenderSchema>;

export const ethnicityOptions = [
  "IC1",
  "IC2",
  "IC3",
  "IC4",
  "IC5",
  "IC6",
  "IC7",
] as const;

export const ethnicitySchema = z.enum(ethnicityOptions);
export type EthnicityType = z.infer<typeof ethnicitySchema>;

export const selfDeclaredEthnicityOptions = [
  "whiteEnglish",
  "whiteIrish",
  "whiteGypsy",
  "whiteRoma",
  "whiteOther",
  "mixedBlackCaribbean",
  "mixedBlackAfrican",
  "mixedAsian",
  "mixedOther",
  "asianEnglish",
  "asianIndian",
  "asianPakistani",
  "asianBangladeshi",
  "asianChinese",
  "asianOther",
  "blackEnglish",
  "blackAfrican",
  "blackCaribbean",
  "blackOther",
  "arab",
  "other",
  "preferNotToSay",
  "unableToAnswer",
] as const;

export const selfDeclaredEthnicitySchema = z.enum(selfDeclaredEthnicityOptions);
export type SelfDeclaredEthnicityType = z.infer<
  typeof selfDeclaredEthnicitySchema
>;

export const pncWarningMarkersOptions = [
  "alleges",
  "ailment",
  "conceals",
  "contagious",
  "drugs",
  "escaper",
  "firearm",
  "f/Impers",
  "m/Impers",
  "impersonates",
  "mentalHealth",
  "selfHarm",
  "suicidal",
  "violent",
  "weapons",
  "explosives",
] as const;

export const pncWarningMarkersSchema = z.enum(pncWarningMarkersOptions);

/**
 * The forces a custody suite can belong to. Wider than the Home Office forces
 * because a suite may be run by any force, including the national and
 * non-territorial ones.
 *
 * Array order is the order the forces are offered in, and runs alphabetically
 * by the label each value carries rather than by the value itself, so a force
 * added here belongs in its alphabetical place and not at the end.
 */
export const responsibleCustodyForceValues = [
  "avonAndSomerset",
  "bedfordshire",
  "britishTransportPolice",
  "cambridgeshire",
  "cheshire",
  "cityOfLondon",
  "civilNuclearConstabulary",
  "cleveland",
  "cumbria",
  "derbyshire",
  "devonAndCornwall",
  "dorset",
  "durham",
  "dyfedPowys",
  "essex",
  "gloucestershire",
  "greaterManchester",
  "gwent",
  "hampshire",
  "hertfordshire",
  "humberside",
  "kent",
  "lancashire",
  "leicestershire",
  "lincolnshire",
  "merseyside",
  "metropolitanPolice",
  "ministryOfDefencePolice",
  "norfolk",
  "northWales",
  "northYorkshire",
  "northamptonshire",
  "northumbria",
  "nottinghamshire",
  "policeScotland",
  "psni",
  "southWales",
  "southYorkshire",
  "staffordshire",
  "suffolk",
  "surrey",
  "sussex",
  "thamesValley",
  "warwickshire",
  "westMercia",
  "westMidlands",
  "westYorkshire",
  "wiltshire",
] as const;

export const responsibleCustodyForceSchema = z.enum(
  responsibleCustodyForceValues,
);
export type ResponsibleCustodyForce = z.infer<
  typeof responsibleCustodyForceSchema
>;

/**
 * A helper function to allow Formik, which is gradually collecting data
 * a keystroke at a time, to handle various "nullish" values like ["",
 * null, undefined], and make them null.
 */
export function nullableSchema<S extends ZodType>(strictFieldSchema: S) {
  const nullishValues: unknown[] = ["", null, undefined];

  return z.preprocess((val) => {
    if (nullishValues.includes(val)) {
      return null;
    } else {
      return val;
    }
  }, strictFieldSchema.nullable());
}

/**
 * Creates a permissive schema from a collection of strict field schemas.
 * It allows all values to be nullable strings, and co-erces empty strings to null.
 *
 * @param strictFieldSchemas A collection of strict field schemas
 * @returns A Zod schema containing all of the field schemas but made permissive.
 */
export const createPermissiveSchema = <T extends Record<string, ZodType>>(
  strictFieldSchemas: T,
) => {
  const existingEntries = Object.entries(strictFieldSchemas) as {
    [K in keyof T]-?: [K, T[K]];
  }[keyof T][];

  const permissiveEntries = existingEntries.map(([key, value]) => [
    key,
    nullableSchema(value),
  ]);

  const newObject = Object.fromEntries(permissiveEntries) as {
    [K in keyof T]-?: ReturnType<typeof nullableSchema<T[K]>>;
  };

  return z.object(newObject);
};

/**
 * Creates initial values for Formik based on a permissive schema.
 *
 * @param strictFieldSchemas An object whose members are strict field schemas
 * @returns Initial values for a form that matches the permissive schema as created by createPermissiveSchema.
 */
export const createInitialValues = <T extends Record<string, ZodType>>(
  strictFieldSchemas: T,
  storedValues?: Record<keyof T, unknown>,
) => {
  const emptyString = "";

  const existingEntries = Object.entries(strictFieldSchemas);

  const permissiveEntries = existingEntries.map(([key, _value]) => [
    key,
    storedValues?.[key] ?? emptyString,
  ]);

  const newObject = Object.fromEntries(permissiveEntries) as {
    [K in keyof T]-?: z.infer<T[K]>;
  };

  return newObject;
};
